import { useCallback, useEffect, useMemo, useRef } from "react";
import api from "utils/api";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { useRoutesCreationModeV3 } from "../../RoutesCreationModeV3/hooks/useRoutesCreationModeV3";
import type {
  RouteCreationRow,
  RouteCreationStep as FormRouteCreationStep,
} from "../../RoutesCreationModeV3/hooks/types";
import type { RouteCreationOverlayState } from "components/SmartSurveyor/ReviewPage/FloorPlanPanelNew/SvgOverlay/RoutesCreationLayer/types";
import { buildScaledRoute } from "./utils/buildScaledRoute";
import { buildOverlayRoutes } from "./utils/buildOverlayRoutes";
import { useNodeDragging } from "./useNodeDragging";
import type {
  CreatedRouteResponse,
  UseRoutesCreationModeOrchestrationArgs,
  RoutesCreationModeOrchestrationResult,
} from "./types";

const DOUBLE_CLICK_THRESHOLD_MS = 300;

export const useRoutesCreationModeOrchestration = ({
  selectedPlan,
  routeGroupLabels,
  byGroupLabel,
  onBulkCreateRoutes,
  floorPlanPanelHandlers,
  interactionMode,
  isDrawingRoute,
  isEditingRoute,
  creationContext,
  startDrawingRoute,
  stopDrawingRoute,
  setRouteCreationStep,
  setRouteCreationGroupName,
  startEditingRoute,
  stopEditingRoute,
  setSelectedGroupLabel,
  setSelectedRouteId,
}: UseRoutesCreationModeOrchestrationArgs): RoutesCreationModeOrchestrationResult => {
  const lastClickTimestampRef = useRef<number>(0);

  // Embedded calibration counts as creation mode; standalone re-calibration
  // doesn't, so the user stays on the cable-groups list while picking points.
  const isEmbeddedCalibrating =
    interactionMode.kind === "calibrating" &&
    interactionMode.origin === "embedded";

  const isRouteCreationMode =
    isDrawingRoute || isEditingRoute || isEmbeddedCalibrating;

  const stateMachineRouteStep =
    interactionMode.kind === "drawing-route" ? interactionMode.step : null;

  const creationStep: FormRouteCreationStep =
    stateMachineRouteStep === "group-selection"
      ? "group-selection"
      : "adding-elements";

  const selectedRouteTypeName = creationContext?.selectedGroupName ?? "";
  const creationContextPageIndex = creationContext?.pageIndex ?? null;

  // `0` = uncalibrated. Saving is gated until set.
  const pageMetersPerPdfPoint = useMemo(
    () =>
      selectedPlan?.metersPerPdfPointByPageIndex.get(
        creationContextPageIndex ?? -1,
      ) ?? 0,
    [selectedPlan, creationContextPageIndex],
  );

  const openRouteCreationMode = useCallback(() => {
    if (!selectedPlan) return;
    startDrawingRoute({ pageIndex: 0 });
  }, [selectedPlan, startDrawingRoute]);

  const closeRouteCreationMode = stopDrawingRoute;

  const handleFormCreationStepChange = useCallback(
    (step: "group-selection" | "adding-elements") => {
      setRouteCreationStep(step === "adding-elements" ? "drawing" : step);
    },
    [setRouteCreationStep],
  );

  const handleSaveAll = useCallback(
    async (rows: RouteCreationRow[]) => {
      if (!selectedPlan || creationContextPageIndex === null) return;
      const pageId = selectedPlan.pageIdByIndex.get(creationContextPageIndex);
      if (pageId === undefined) return;

      const created: CreatedRouteResponse[] =
        await api.bulkCreateQuantitySurveyorRoutes({
          floorPlanId: selectedPlan.floorPlanId,
          pageId,
          routes: rows.map((row) => ({
            routeTypeName: row.routeTypeName.trim(),
            name: row.name || undefined,
            breaker: row.breaker || undefined,
            distancePx: 0,
            meters: row.polylineLength * pageMetersPerPdfPoint,
            nodes: row.nodes,
            edges: row.edges,
          })),
        });
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_ROUTES_CREATED, {
        routeCount: created.length,
        newGroupCount: new Set(
          created
            .map((response) => response.routeTypeName)
            .filter((routeTypeName) => !byGroupLabel[routeTypeName]),
        ).size,
      });

      const bulkRouteEntries = created.map((response, index) => {
        const row = rows[index];
        return {
          groupLabel: response.routeTypeName,
          routeTypeId: response.routeTypeId,
          scaledRoute: buildScaledRoute({
            row,
            meters: row.polylineLength * pageMetersPerPdfPoint,
            response,
            pageIndex: creationContextPageIndex,
            sibling: byGroupLabel[response.routeTypeName]?.routes[0],
          }),
        };
      });

      onBulkCreateRoutes({
        floorPlanId: selectedPlan.floorPlanId,
        routes: bulkRouteEntries,
      });

      const firstCreatedRoute = bulkRouteEntries[0]?.scaledRoute ?? null;
      if (firstCreatedRoute) {
        setSelectedGroupLabel(firstCreatedRoute.cable_type);
        setSelectedRouteId(firstCreatedRoute.id);
        const firstNode = firstCreatedRoute.nodes[0];
        if (firstNode) {
          floorPlanPanelHandlers?.current?.zoomToObject({
            x: firstNode.x,
            y: firstNode.y,
          });
        }
      }

      closeRouteCreationMode();
    },
    [
      selectedPlan,
      creationContextPageIndex,
      pageMetersPerPdfPoint,
      byGroupLabel,
      onBulkCreateRoutes,
      floorPlanPanelHandlers,
      setSelectedGroupLabel,
      setSelectedRouteId,
      closeRouteCreationMode,
    ],
  );

  const creationModeState = useRoutesCreationModeV3({
    routeTypeOptions: routeGroupLabels,
    creationStep,
    selectedRouteTypeName,
    pageMetersPerPdfPoint,
    onCreationStepChange: handleFormCreationStepChange,
    onSelectedRouteTypeNameChange: setRouteCreationGroupName,
    onSaveAll: handleSaveAll,
    onCancel: closeRouteCreationMode,
  });

  const { resetState } = creationModeState;

  const activeRow =
    creationModeState.activeIndex >= 0 &&
    creationModeState.activeIndex < creationModeState.routes.length
      ? creationModeState.routes[creationModeState.activeIndex]
      : null;

  // Drawing: user is laying down nodes for a new route.
  // Editing: user is mutating an existing in-progress route (drag/insert).
  const isRouteDrawingActive =
    isDrawingRoute &&
    stateMachineRouteStep === "drawing" &&
    creationModeState.isDrawingActive;

  const isRouteEditingActive =
    isRouteCreationMode &&
    !creationModeState.isDrawingActive &&
    !!activeRow &&
    activeRow.nodes.length > 0;

  const { handleNodeMouseDown, handleSegmentMouseDown, applyDragPosition } =
    useNodeDragging({
      isEditingActive: isRouteEditingActive,
      creationModeState,
    });

  const handleRouteMapClick = useCallback(
    (pos: { x: number; y: number }) => {
      if (!isRouteDrawingActive) return;

      const now = Date.now();
      const isDoubleClick =
        now - lastClickTimestampRef.current < DOUBLE_CLICK_THRESHOLD_MS;
      lastClickTimestampRef.current = now;

      if (isDoubleClick) {
        creationModeState.finalizeActiveRoute();
        return;
      }

      creationModeState.appendNodeToActiveRoute(pos);
    },
    [isRouteDrawingActive, creationModeState],
  );

  const handleRouteMapMouseMove = useCallback(
    (pos: { x: number; y: number } | null) => {
      // While a node is being dragged, the move belongs to the drag.
      if (pos && applyDragPosition(pos)) return;

      // Outside of route creation mode, just keep hover cleared.
      if (!isRouteCreationMode) {
        if (creationModeState.hoverPosition !== null) {
          creationModeState.setHoverPosition(null);
        }
        return;
      }

      creationModeState.setHoverPosition(pos);
    },
    [applyDragPosition, isRouteCreationMode, creationModeState],
  );

  const routeCreationOverlay = useMemo<RouteCreationOverlayState | null>(() => {
    if (!isRouteCreationMode) return null;

    const overlayRoutes = buildOverlayRoutes({
      routes: creationModeState.routes,
      byGroupLabel,
      activeIndex: creationModeState.activeIndex,
    });

    const isActiveRouteInProgress = !!activeRow && !activeRow.isFinalized;

    return {
      routes: overlayRoutes,
      hoverPosition:
        isRouteDrawingActive && isActiveRouteInProgress
          ? creationModeState.hoverPosition
          : null,
      isEditable: isRouteEditingActive,
      selectedNodeId: creationModeState.selectedNodeId,
      onNodeMouseDown: handleNodeMouseDown,
      onSegmentMouseDown: handleSegmentMouseDown,
    };
  }, [
    isRouteCreationMode,
    isRouteDrawingActive,
    isRouteEditingActive,
    activeRow,
    creationModeState.routes,
    creationModeState.activeIndex,
    creationModeState.hoverPosition,
    creationModeState.selectedNodeId,
    handleNodeMouseDown,
    handleSegmentMouseDown,
    byGroupLabel,
  ]);

  // Mirror the form-derived editing state into the reducer kind so consumers
  // that switch on `interactionMode.kind` see one mutually-exclusive mode.
  useEffect(() => {
    if (isRouteEditingActive && !isEditingRoute) {
      startEditingRoute();
      return;
    }
    if (!isRouteEditingActive && isEditingRoute) {
      stopEditingRoute();
    }
  }, [
    isRouteEditingActive,
    isEditingRoute,
    startEditingRoute,
    stopEditingRoute,
  ]);

  // Cleanup transient inner state whenever the reducer leaves creation mode
  // (tab switch, plan switch, sidebar mode change all dispatch resetToIdle).
  useEffect(() => {
    if (!isRouteCreationMode) {
      resetState();
    }
  }, [isRouteCreationMode, resetState]);

  return {
    isRouteCreationMode,
    openRouteCreationMode,
    closeRouteCreationMode,
    creationModeState,
    isRouteDrawingActive,
    isRouteEditingActive,
    routeCreationOverlay,
    handleRouteMapClick,
    handleRouteMapMouseMove,
    handleNodeMouseDown,
    handleSegmentMouseDown,
    pageMetersPerPdfPoint,
  };
};
